import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth, useUserMap } from "@/context/AuthContext";
import { inr } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { CLIENT_STATUS, CLIENT_STATUSES, BILLING_CYCLES, BILLING_BASIS, PAYMENT_METHODS, JOB_ROLE_LABEL, label } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Search, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", contact_person: "", phone: "", whatsapp: "", email: "", address: "", website: "",
  instagram: "", facebook: "", account_manager_id: "", start_date: "", status: "active",
  services: [], package: "",
  commitment: { static: 0, carousel: 0, reel: 0, video: 0, story: 0, shoot: 0, custom: [] },
  billing_cycle: "monthly", billing_type: "postpaid", billing_basis: "advance", custom_cycle_months: 1,
  service_start_date: "", billing_active: true, billing_day: 5, monthly_amount: 0, gst_applicable: false, gstin: "",
  payment_method: "bank_transfer", billing_notes: "",
};

function NumField({ label: l, value, onChange }) {
  return (
    <div>
      <Label className="text-xs text-zinc-500">{l}</Label>
      <Input type="number" min={0} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-1" />
    </div>
  );
}

export function ClientDialog({ open, onOpenChange, initial, onSaved }) {
  const { users } = useAuth();
  const [f, setF] = useState(initial || empty);
  const [servicesText, setServicesText] = useState("");
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    const base = initial || empty;
    setF(base);
    setServicesText((base.services || []).join(", "));
  }, [initial, open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setC = (k, v) => setF((p) => ({ ...p, commitment: { ...p.commitment, [k]: v } }));
  const managers = users.filter((u) => u.active && u.job_role === "social_media_handler");

  const save = async () => {
    if (!f.name.trim()) return toast.error("Client name is required");
    setSaving(true);
    try {
      const services = servicesText.split(",").map((x) => x.trim()).filter(Boolean);
      const payload = { ...f, services, account_manager_id: f.account_manager_id || null };
      if (initial?.id) await api.put(`/clients/${initial.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(initial?.id ? "Client updated" : "Client created");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial?.id ? "Edit Client" : "Add Client"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-2">
          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Basic Information</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Business / Client Name *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="client-name-input" /></div>
              <div><Label className="text-xs text-zinc-500">Contact Person</Label><Input value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs text-zinc-500">Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs text-zinc-500">WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs text-zinc-500">Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs text-zinc-500">Instagram</Label><Input value={f.instagram} onChange={(e) => set("instagram", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs text-zinc-500">Facebook</Label><Input value={f.facebook} onChange={(e) => set("facebook", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs text-zinc-500">Website</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} className="mt-1" /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Address</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-1" /></div>
            </div>
          </div>

          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Agency Information</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-zinc-500">Account Manager</Label>
                <Select value={f.account_manager_id || "none"} onValueChange={(v) => set("account_manager_id", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {JOB_ROLE_LABEL[m.job_role] || "Social Media Handler"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-zinc-500">Start / Joining Date</Label><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className="mt-1" /></div>
              <div>
                <Label className="text-xs text-zinc-500">Status</Label>
                <Select value={f.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLIENT_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-zinc-500">Package</Label><Input value={f.package} onChange={(e) => set("package", e.target.value)} className="mt-1" placeholder="e.g. Premium SMM" /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Services (comma separated)</Label><Input value={servicesText} onChange={(e) => setServicesText(e.target.value)} className="mt-1" placeholder="e.g. SMM, Shoots, Ads" data-testid="client-services-input" /></div>
            </div>
          </div>

          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Monthly Commitment</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <NumField label="Static Posts" value={f.commitment.static} onChange={(v) => setC("static", v)} />
              <NumField label="Carousels" value={f.commitment.carousel} onChange={(v) => setC("carousel", v)} />
              <NumField label="Reels" value={f.commitment.reel} onChange={(v) => setC("reel", v)} />
              <NumField label="Videos" value={f.commitment.video} onChange={(v) => setC("video", v)} />
              <NumField label="Stories" value={f.commitment.story} onChange={(v) => setC("story", v)} />
              <NumField label="Shoot Days" value={f.commitment.shoot} onChange={(v) => setC("shoot", v)} />
            </div>
          </div>

          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Billing</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div><Label className="text-xs text-zinc-500">Cycle Amount (₹)</Label><Input type="number" value={f.monthly_amount} onChange={(e) => set("monthly_amount", Number(e.target.value))} className="mt-1" data-testid="client-amount-input" /></div>
              <div>
                <Label className="text-xs text-zinc-500">Billing Cycle</Label>
                <Select value={f.billing_cycle} onValueChange={(v) => set("billing_cycle", v)}>
                  <SelectTrigger className="mt-1" data-testid="client-billing-cycle"><SelectValue /></SelectTrigger>
                  <SelectContent>{BILLING_CYCLES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {f.billing_cycle === "custom" && (
                <div><Label className="text-xs text-zinc-500">Custom Cycle (months)</Label><Input type="number" min={1} value={f.custom_cycle_months} onChange={(e) => set("custom_cycle_months", Number(e.target.value))} className="mt-1" /></div>
              )}
              <div>
                <Label className="text-xs text-zinc-500">Billing Basis</Label>
                <Select value={f.billing_basis} onValueChange={(v) => set("billing_basis", v)}>
                  <SelectTrigger className="mt-1" data-testid="client-billing-basis"><SelectValue /></SelectTrigger>
                  <SelectContent>{BILLING_BASIS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Service Start Date</Label>
                <Input type="date" value={f.service_start_date || ""} onChange={(e) => set("service_start_date", e.target.value)} className="mt-1" data-testid="client-service-start" />
                <p className="mt-1 text-[11px] text-zinc-400">Schedule is generated from this date</p>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Billing Day</Label>
                <Input type="number" min={1} max={28} value={f.billing_day} onChange={(e) => set("billing_day", Number(e.target.value))} className="mt-1" />
                <p className="mt-1 text-[11px] text-zinc-400">Due date is this day of the billing cycle</p>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Payment Method</Label>
                <Select value={f.payment_method} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={f.billing_active} onCheckedChange={(v) => set("billing_active", v)} id="billing_active" data-testid="client-billing-active" />
                <Label htmlFor="billing_active" className="text-sm">Billing Active</Label>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={f.gst_applicable} onCheckedChange={(v) => set("gst_applicable", v)} id="gst" />
                <Label htmlFor="gst" className="text-sm">GST Applicable</Label>
              </div>
              {f.gst_applicable && <div><Label className="text-xs text-zinc-500">GSTIN</Label><Input value={f.gstin} onChange={(e) => set("gstin", e.target.value)} className="mt-1" /></div>}
              <div className="sm:col-span-2 lg:col-span-3"><Label className="text-xs text-zinc-500">Billing Notes</Label><Textarea value={f.billing_notes} onChange={(e) => set("billing_notes", e.target.value)} className="mt-1" rows={2} /></div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="client-save-button">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const { isManager } = useAuth();
  const qc = useQueryClient();
  const umap = useUserMap();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [dialog, setDialog] = useState(false);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => (await api.get("/clients")).data,
  });

  const filtered = clients.filter((c) => {
    if (status !== "all" && c.status !== status) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Clients</h1>
          <p className="mt-1 text-sm text-zinc-500">{clients.length} clients · central hub for every account</p>
        </div>
        {isManager && (
          <Button onClick={() => setDialog(true)} data-testid="add-client-button"><Plus className="mr-2 h-4 w-4" />Add Client</Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search clients..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="client-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="client-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {CLIENT_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => (
            <Card
              key={c.id}
              onClick={() => navigate(`/clients/${c.id}`)}
              data-testid={`client-card-${c.id}`}
              className="cursor-pointer rounded-lg border-zinc-200 p-5 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-zinc-100 text-zinc-500"><Building2 className="h-5 w-5" /></div>
                  <div>
                    <div className="font-heading font-semibold text-zinc-900">{c.name}</div>
                    <div className="text-xs text-zinc-500">{c.package || "—"}</div>
                  </div>
                </div>
                <Badge color={CLIENT_STATUS[c.status]?.color} dot>{label(CLIENT_STATUS, c.status)}</Badge>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-zinc-400">{BILLING_CYCLES.find((b) => b.v === c.billing_cycle)?.l || "Cycle"} Amount</div>
                  <div className="font-mono font-semibold">{inr(c.monthly_amount)}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-400">Account Manager</div>
                  <div className="truncate text-sm">{umap[c.account_manager_id]?.name || "—"}</div>
                </div>
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <div className="col-span-full py-12 text-center text-zinc-400">No clients found</div>}
        </div>
      )}

      <ClientDialog open={dialog} onOpenChange={setDialog} onSaved={() => qc.invalidateQueries({ queryKey: ["clients"] })} />
    </div>
  );
}
